import { supabase } from '../../infrastructure/supabase/supabaseClient';

export interface IdentityProfileInput {
  nombres_completos: string;
  apellidos_completos: string;
  perfil_dni: string;
  perfil_fecha_nacimiento: string; // YYYY-MM-DD
  avatar_url?: string | null;
}

export class ProfileService {
  static async updateIdentity(data: IdentityProfileInput): Promise<void> {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    
    if (authError || !user) {
      throw new Error("No estás autenticado. La sesión expiró.");
    }

    const { error: updateError } = await supabase
      .from('perfiles_usuario')
      .update({
        nombres_completos: data.nombres_completos,
        apellidos_completos: data.apellidos_completos,
        perfil_dni: data.perfil_dni,
        perfil_fecha_nacimiento: data.perfil_fecha_nacimiento,
        ...(data.avatar_url !== undefined && { avatar_url: data.avatar_url })
      })
      .eq('perfil_usuario_id', user.id);

    if (updateError) {
      console.error("Error al actualizar perfil:", updateError);
      throw new Error("No se pudo actualizar el perfil. Verifica tu conexión.");
    }
  }

  static async getProfile(): Promise<any> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return null;

    const { data, error } = await supabase
      .from('perfiles_usuario')
      .select('*')
      .eq('perfil_usuario_id', session.user.id)
      .single();
    
    if (error) return null;
    return data;
  }

  static async isClinicalProfessional(): Promise<boolean> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) {
      console.log("[ProfileService] No hay usuario autenticado en sesión.");
      return false;
    }

    // 1. Verificación directa en el perfil (flag rápido)
    const { data: profile, error: profileError } = await supabase
      .from('perfiles_usuario')
      .select('es_profesional_verificado')
      .eq('perfil_usuario_id', session.user.id)
      .single();

    if (profileError) {
      console.log("[ProfileService] Error leyendo perfiles_usuario:", profileError.message);
    }

    if (profile?.es_profesional_verificado) {
      return true;
    }

    // 2. Verificación profunda en los roles polimórficos
    const { data, error } = await supabase
      .from('usuario_roles_polimorficos')
      .select(`
        rol_activo,
        roles_polimorficos_maestros!inner(es_clinico)
      `)
      .eq('perfil_usuario_id', session.user.id)
      .eq('rol_activo', true);

    if (error) {
      console.log("[ProfileService] Error leyendo usuario_roles_polimorficos:", error.message);
      return false;
    }
    
    // Filtrar localmente por si la sintaxis de PostgREST falla
    const hasClinicalRole = data?.some((row: any) => row.roles_polimorficos_maestros?.es_clinico === true);
    
    if (hasClinicalRole) {
      return true;
    }

    return false;
  }
}
